import type { Components as EntityComponents } from '@epilot/entity-client'

import type { BLOCK_TYPE } from '../enums'
import type { ReferenceId } from '../ids'
import type { BlockReference, ContextParameterReference } from '../references'

import type { IBlock, BlockBaseSettings } from './base'

import type {
  MultipleChoiceBlock,
  NumberInputBlock,
  SingleChoiceBlock,
  TextInputBlock,
  DateInputBlock
} from '.'

export enum ENTITY_ATTRIBUTE_MODE {
  ENTITY_BASED = 'entity_finder',
  AUTH_BASED = 'logged_in_contact',
  CONTEXT_BASED = 'context_data'
}

/**
 * Union of all supported EntityAttribute blocks.
 *
 * These are the blocks used to render according to the EntityAttribute type.
 */
type EntityAttributeRenderBlocks =
  | DateInputBlock
  | SingleChoiceBlock
  | MultipleChoiceBlock
  | NumberInputBlock
  | TextInputBlock

type SharedSettings = {
  /** Restricts the inputs to read only */
  readOnly: boolean
  /**
   * Slug of the associated Entity.
   *
   * This is used to determine the Entity to which the attribute belongs,
   * and is used to read/write the attribute value.
   */
  entitySlug: boolean
  /**
   * Please check {@link EntityAttributeRenderBlocks} to see which types <> blocks are supported.
   *
   * TODO: Not store name/labels as part of the Journey config
   */
  entityAttribute?: Pick<
    EntityComponents.Schemas.Attribute,
    'id' | 'name' | 'label' | 'type'
  >
  /**
   * Determines how the underlying control should behave/render.
   *
   * The value of {@link SharedSettings.entityAttribute.type} determines which block type
   * gets rendered by the EntityAttribute block, which acts as a wrapper.
   *
   * TODO: Further coerce TS according to the type of the attribute
   */
  renderSettings: Partial<EntityAttributeRenderBlocks['settings']>
}

export interface EntityAttributeContextReference
  extends ContextParameterReference {
  meaning: 'source'
}

export interface EntityAttributeFinderReference extends BlockReference {
  meaning: 'source'
}

export interface IEntityAttributeBlock extends IBlock {
  type: BLOCK_TYPE.ENTITY_ATTRIBUTE
  settings: BlockBaseSettings & {
    /** Restricts the inputs to read only */
    readOnly: boolean
    /** Determines the source of information */
    mode: ENTITY_ATTRIBUTE_MODE
  }
}

export interface EntityBasedEntityAttributeBlock extends IEntityAttributeBlock {
  type: BLOCK_TYPE.ENTITY_ATTRIBUTE
  settings: BlockBaseSettings &
    SharedSettings & {
      mode: ENTITY_ATTRIBUTE_MODE.ENTITY_BASED
    }
  /** Single entity finder block reference */
  references: Record<ReferenceId, EntityAttributeFinderReference>
}

export interface ContextBasedEntityAttributeBlock
  extends IEntityAttributeBlock {
  type: BLOCK_TYPE.ENTITY_ATTRIBUTE
  settings: BlockBaseSettings &
    SharedSettings & {
      mode: ENTITY_ATTRIBUTE_MODE.CONTEXT_BASED
    }
  /** Single context parameter reference */
  references: Record<ReferenceId, EntityAttributeContextReference>
}

export interface AuthBasedEntityAttributeBlock extends IEntityAttributeBlock {
  type: BLOCK_TYPE.ENTITY_ATTRIBUTE
  settings: BlockBaseSettings &
    SharedSettings & {
      mode: ENTITY_ATTRIBUTE_MODE.AUTH_BASED
    }
  /** Contains no references */
  references: {}
}

export type EntityAttributeBlock =
  | EntityBasedEntityAttributeBlock
  | ContextBasedEntityAttributeBlock
  | AuthBasedEntityAttributeBlock
